import React, { useState } from 'react';
import { alpha, Box, Collapse, Stack, Theme } from '@mui/material';
import { ThinkingContentItem } from 'app/concierge/types';
import { renderMarkdown } from 'tools/safeHtml';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';

interface IProps {
    content: ThinkingContentItem[];
    loading: boolean;
}

export default function StreamThinkingBlock({ content, loading }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const [expanded, setExpanded] = useState(true);

    if (!loading) {
        return null;
    }

    const combinedText = content.map((item) => item.value).join('\n');

    return (
        <Box
            sx={{
                my: 1,
                overflow: 'hidden',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                    borderRadius: expanded ? '4px 4px 0 0' : '4px',
                    px: 2,
                    py: 1,
                    display: 'inline-flex',
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                    },
                    bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                }}
            >
                <Box sx={{ fontSize: '0.75rem' }}>{t('thinking.stream_button')}</Box>
                <IconMi
                    icon={expanded ? 'chevron-up' : 'chevron-down'}
                    sx={{ fontSize: '16px', color: 'text.secondary' }}
                />
            </Stack>
            <Collapse in={expanded}>
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        fontSize: '0.8125rem',
                        '& p': { m: 0, mb: 0.5 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': { m: 0, mb: 0.5, mt: 1 },
                        '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
                        '& ul, & ol': { m: 0, pl: 2, mb: 0.5 },
                        '& li': { mb: 0.25 },
                        bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                        borderRadius: '0px 4px 4px 4px',
                    }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(combinedText) }}
                />
            </Collapse>
        </Box>
    );
}
